import { FactCheck, Hive, Map, NotificationsActive, PeopleAlt, Task } from "@mui/icons-material";
import { FC, ReactNode } from "react";
import { Navigate, Route, RouteProps, Routes } from "react-router-dom";
import { MAIN_NODES, PERMISSIONS_ACTIONS } from "../common/config/permissions";
import { useAuth } from "../common/hooks/useAuth";
import AreasExpertise from "../pages/private/AreasExpertise";
import BeehiveDetails from "../pages/private/Beehives/BeehiveDetails/BeehiveDetails";
import Beehives from "../pages/private/Beehives/Beehives";
import Notifications from "../pages/private/Notifications";
import Permissions from "../pages/private/Permissions/Permissions";
import Users from "../pages/private/Users";
import ChangePassword from "../pages/public/ChangePassword";
import Login from "../pages/public/Login";
import NotFound from "../pages/public/NotFound";
import Privacy from "../pages/public/Privacy";
import ProjectPreview from "../pages/public/ProjectPreview";
import Register from "../pages/public/Register";
import SuccessRedirect from "../pages/public/SuccessRedirect";
import TermsAndConditions from "../pages/public/TermsAndConditions";
import Redirect from "./Redirect";

const BASE_KEY = "BEE2GETHER_";

export const navOptionsRoutes: (RouteProps & {
    label: string,
    childrenPaths?: RouteProps[],
    icon?: ReactNode,
    permissions: string[]
})[] = [
        {
            id: BASE_KEY + "beehives-page",
            path: "beehives",
            label: "sidebar.beehives",
            icon: <Hive />,
            childrenPaths: [
                {
                    id: BASE_KEY + "beehives-list-page",
                    path: "",
                    element: <Beehives />
                },
                {
                    id: BASE_KEY + "beehives-details-page",
                    path: ":id",
                    element: <BeehiveDetails />,
                },
            ],
            permissions: [MAIN_NODES.BEEHIVES + "_" + PERMISSIONS_ACTIONS.VIEW]
        },
        {
            id: BASE_KEY + "notifications-list-page",
            path: "notifications",
            element: <Notifications />,
            icon: <NotificationsActive />,
            label: "sidebar.notifications",
            permissions: [
                MAIN_NODES.BEEHIVES_ALERTS + "_" + PERMISSIONS_ACTIONS.VIEW,
                MAIN_NODES.BEEHIVES + "_" + PERMISSIONS_ACTIONS.VIEW
            ]
        },
        {
            id: BASE_KEY + "users-list-page",
            path: "users",
            element: <Users />,
            icon: <PeopleAlt />,
            label: "sidebar.users",
            permissions: [
                MAIN_NODES.USERS + "_" + PERMISSIONS_ACTIONS.VIEW,
                MAIN_NODES.USERS + "_" + PERMISSIONS_ACTIONS.CREATE,
                MAIN_NODES.USERS + "_" + PERMISSIONS_ACTIONS.EDIT
            ]
        },
        {
            id: BASE_KEY + "areas-expertise-page",
            path: "areas-expertise",
            element: <AreasExpertise />,
            icon: <Map />,
            label: "sidebar.areas",
            permissions: [MAIN_NODES.AREAS_EXPERTISE + "_" + PERMISSIONS_ACTIONS.VIEW]
        },
        {
            id: BASE_KEY + "permissions",
            path: "permissions",
            element: <Permissions />,
            icon: <FactCheck />,
            label: "sidebar.permissions",
            permissions: [
                MAIN_NODES.ROLES + "_" + PERMISSIONS_ACTIONS.VIEW,
                MAIN_NODES.ROLES + "_" + PERMISSIONS_ACTIONS.CREATE,
                MAIN_NODES.ROLES + "_" + PERMISSIONS_ACTIONS.EDIT,
            ]
        },
        {
            id: BASE_KEY + "project-preview-page",
            path: "project",
            element: <ProjectPreview />,
            icon: <Task />,
            label: "sidebar.project",
            permissions: []
        }
    ]


interface IRoutesProps { }

// checking if the current user is authenticated
const ProtectedRoute = () => {
    const { loginData } = useAuth();
    return !loginData ? <Navigate to="/login" replace /> : <Redirect />;
};

const Router: FC<IRoutesProps> = () => {
    const { permissionsData } = useAuth();

    const isRoleAuthorized = (route: RouteProps & {
        label: string,
        childrenPaths?: RouteProps[],
        icon?: ReactNode,
        permissions: string[]
    }) => {
        if (permissionsData) {
            let isPermitted: boolean = false
            Object.keys(permissionsData).map(node => {
                permissionsData[node].map(perm => {
                    if (route.permissions.length === 0)
                        isPermitted = true
                    else if (route.permissions.length === 1 && perm.includes(route.permissions[0]))
                        isPermitted = true
                    else if (route.permissions.length > 1 && perm.includes(route.permissions[0])) {
                        if (
                            Object.keys(permissionsData).includes(route.permissions[1].split("_")[0]) &&
                            permissionsData[route.permissions[1].split("_")[0]].find(p => p.includes(route.permissions[1]))
                        )
                            isPermitted = true
                    }
                })
            })
            return isPermitted
        }
        return false;
    }

    const setFirstRouteAvailable = () => {
        return navOptionsRoutes.find(route => isRoleAuthorized(route))?.path || "/beehives"
    }

    return <Routes>
        <Route path="404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="404" replace />} />
        <Route path="login" element={<Login />} />
        <Route path="project-preview" element={<ProjectPreview />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="register" element={<Register />} />
        <Route path="set-new-password" element={<ChangePassword />} />
        <Route path="account-activated" element={<SuccessRedirect />} />
        <Route path="password-set" element={<SuccessRedirect />} />
        <Route path="" element={<ProtectedRoute />}>
            <Route index element={<Navigate to={setFirstRouteAvailable()} replace />} />
            {
                navOptionsRoutes.map(route =>
                    isRoleAuthorized(route) ?
                        <Route key={route.id} path={route.path} element={route.element}>
                            {route.childrenPaths?.map(children => <Route {...children} key={children.path} />)}
                        </Route> : null
                )
            }
        </Route>
    </Routes>
}

export default Router;